/**
 * followUpVocabulary — cómo se cuenta un seguimiento, en un solo sitio.
 *
 * Vivía dentro de `FollowUpBoard`. Ahora el Centro de acción enseña los
 * mismos seguimientos, y dos pantallas que traduzcan `first_contact` o
 * `whatsapp` por su cuenta acaban diciendo cosas distintas del mismo hecho.
 * El tablero sigue siendo el dueño del layout; el idioma es de aquí.
 *
 * Puro: sin React, sin Phosphor, sin fechas implícitas. Quien pinte iconos
 * los importa donde los use.
 */

/** Mantenido en sync con backend/server.py `_FOLLOWUP_RULES_BY_STAGE.action_type`. */
export const ACTION_LABELS = {
  first_contact: "Primer contacto",
  follow_up_after_message: "Seguimiento",
  call_reminder: "Recordatorio de llamada",
  send_dossier: "Enviar dossier",
  onboarding: "Onboarding",
  reactivation: "Reactivación",
  reply_to_lead: "Responder al lead",
};

export const CHANNEL_LABEL = {
  whatsapp: "WhatsApp",
  email: "Email",
  call: "Llamada",
  instagram: "Instagram",
  other: "Otro",
};

/**
 * El código de acción, en cristiano. Un código que no conocemos se
 * humaniza en vez de enseñarse como slug: el backend puede añadir reglas
 * sin que la interfaz enseñe `send_dossier_v2`.
 */
export function humaniseActionType(code) {
  if (!code) return "Seguimiento";
  if (ACTION_LABELS[code]) return ACTION_LABELS[code];
  return String(code)
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

/** "12 sept, 10:30" — o null si no hay fecha. Nunca inventa una. */
export function fmtAbsolute(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
}

/** "Vencida hace 3 h" / "Vence hoy a las 17:00" / "Vence en 4 d". */
export function relativeLabel(iso, now) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / (1000 * 60));
  const diffH = diffMs / (1000 * 60 * 60);

  if (diffMin <= 0) {
    const abs = Math.abs(diffMin);
    if (abs < 60) return `Vencida hace ${abs} min`;
    const absH = Math.round(Math.abs(diffH));
    if (absH < 24) return `Vencida hace ${absH} h`;
    return `Vencida hace ${Math.round(absH / 24)} d`;
  }

  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date(tomorrow); dayAfter.setDate(dayAfter.getDate() + 1);

  if (d < tomorrow) {
    return `Vence hoy a las ${d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`;
  }
  if (d < dayAfter) {
    return `Vence mañana a las ${d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`;
  }
  const days = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 30) return `Vence en ${days} d`;
  return `Vence el ${d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}`;
}

/** "WhatsApp · 12 sept, 10:30", o null si nunca hubo interacción. */
export function lastInteractionLabel(at, channel) {
  const when = fmtAbsolute(at);
  if (!when) return null;
  const who = CHANNEL_LABEL[channel];
  return who ? `${who} · ${when}` : when;
}
